/**
 * Helpers puros da busca global FTS (Cmd+K) — story E3.2.1.
 *
 * Sem React/DOM: navegação por tipo, metadados de grupo e highlight client-side.
 * Testável isoladamente em ``__tests__/globalSearch.test.ts``.
 */
import { CheckSquare, FileText, MessageSquare, Newspaper, type LucideIcon } from "lucide-react";

export type SearchableEntityType = "document" | "chat_message" | "task" | "feed_post";

/**
 * Fonte única: cada tipo pesquisável → rota de coleção do módulo.
 *
 * Os keys deste mapa são EXATAMENTE os ``entity_types`` enviados ao endpoint
 * (ver ``useGlobalSearch``), garantindo por construção que todo resultado tem
 * destino navegável — zero link morto. ``calendar_event`` fica de fora: não há
 * rota standalone de calendário e a partição está vazia neste branch (E2 não
 * mergeado). Quando o Calendário ganhar rota, basta adicionar a entrada aqui.
 */
export const ENTITY_TYPE_ROUTES: Record<SearchableEntityType, string> = {
  document: "/documentos/listagem",
  chat_message: "/mensagens",
  task: "/tasks",
  feed_post: "/feed",
};

export const SEARCHABLE_ENTITY_TYPES = Object.keys(ENTITY_TYPE_ROUTES) as SearchableEntityType[];

interface EntityTypeMeta {
  /** Rótulo do grupo no palette (PT). */
  label: string;
  icon: LucideIcon;
}

export const ENTITY_TYPE_META: Record<SearchableEntityType, EntityTypeMeta> = {
  document: { label: "Documentos", icon: FileText },
  chat_message: { label: "Mensagens", icon: MessageSquare },
  task: { label: "Tarefas", icon: CheckSquare },
  feed_post: { label: "Feed", icon: Newspaper },
};

export function isSearchableEntityType(t: string): t is SearchableEntityType {
  return Object.prototype.hasOwnProperty.call(ENTITY_TYPE_ROUTES, t);
}

/** Rota de coleção para o tipo da entidade (null se desconhecido — defensivo). */
export function entityRoute(entityType: string): string | null {
  return isSearchableEntityType(entityType) ? ENTITY_TYPE_ROUTES[entityType] : null;
}

export interface HighlightSegment {
  text: string;
  match: boolean;
}

/**
 * Sentinelas de highlight emitidas pelo backend (``ts_headline``, story E3.2.2).
 *
 * Chars private-use (U+E000/U+E001): nunca aparecem em texto humano real e NÃO são
 * HTML — o backend envolve cada match nelas e o frontend remonta ``<mark>`` via
 * React (texto escapado), o que torna o highlight XSS-safe POR CONSTRUÇÃO (nada de
 * ``dangerouslySetInnerHTML``). Mantê-las em sync com ``HL_OPEN``/``HL_CLOSE`` de
 * ``app/services/search/query_service.py``.
 */
export const HL_OPEN = "";
export const HL_CLOSE = "";

const HL_SENTINEL_RE = new RegExp(`[${HL_OPEN}${HL_CLOSE}]`, "g");

/**
 * Remove as sentinelas de um texto — para checagens de "está vazio?"/``trim``, sem
 * destacar. NÃO usar para renderizar (perde os matches); use ``parseHighlight`` pra
 * isso. Útil pra decidir se um título marcado é, na real, vazio (só sentinelas).
 */
export function stripHighlight(s: string): string {
  return s.replace(HL_SENTINEL_RE, "");
}

/**
 * Quebra um texto JÁ MARCADO pelo servidor em segmentos, sinalizando quais trechos
 * são match (entre ``HL_OPEN`` e ``HL_CLOSE``). As sentinelas são descartadas —
 * nunca vazam pro DOM. O ``join`` dos ``text`` dos segmentos reconstrói o texto SEM
 * as sentinelas (highlight com stemming real, vindo do ``ts_headline``).
 *
 * Defensivo a marcação malformada (sentinela órfã, aninhada, desbalanceada): jamais
 * lança e nunca deixa sentinela no texto exibido. Texto sem sentinela (ex.: resultado
 * fuzzy) → um único segmento não-match.
 */
export function parseHighlight(marked: string): HighlightSegment[] {
  if (!marked) return [];

  const segments: HighlightSegment[] = [];
  let buf = "";
  let inMatch = false;

  const flush = () => {
    if (buf) segments.push({ text: buf, match: inMatch });
    buf = "";
  };

  for (const ch of marked) {
    if (ch === HL_OPEN) {
      // HL_OPEN aninhado (já em match): idempotente, ignora.
      if (!inMatch) {
        flush();
        inMatch = true;
      }
    } else if (ch === HL_CLOSE) {
      // HL_CLOSE órfão (sem OPEN): ignora.
      if (inMatch) {
        flush();
        inMatch = false;
      }
    } else {
      buf += ch;
    }
  }
  flush();

  return segments;
}
